import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  LabelList,
  Legend,
} from "recharts";

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="p-2 bg-white border rounded shadow text-sm text-gray-700">
        <p className="font-semibold text-indigo-700">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} style={{ color: entry.color }}>
            {entry.dataKey === "completed" ? "✅ Completed" : "⏳ Pending"}: <strong>{entry.value}</strong>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const MonthlyReportGraphModal = ({ data, onClose, orderTotals = {} }) => {
  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();

  const groupedByOrder = {};
  (data || []).forEach(item => {
    const date = new Date(item.date);
    if (date.getMonth() !== currentMonth || date.getFullYear() !== currentYear) return;

    const order = item.cut_order_no || `Order-${item.order_id}`;
    const color = item.suborder_colour_name || `Color-${item.sub_order_id}`;

    if (!groupedByOrder[order]) groupedByOrder[order] = {};
    if (!groupedByOrder[order][color]) groupedByOrder[order][color] = 0;

    groupedByOrder[order][color] += item.total_piece;
  });

  const colorPalette = ["#4caf50", "#ff6f61"];

  const allCharts = Object.entries(orderTotals).map(([order, plannedColors]) => {
    let totalPlanned = 0;
    let totalCompleted = 0;

    const chartData = Object.entries(plannedColors).map(([color, planned]) => {
      const completed = groupedByOrder?.[order]?.[color] || 0;
      const pending = Math.max(planned - completed, 0);

      totalPlanned += planned;
      totalCompleted += completed;

      return {
        label: color,
        completed,
        pending,
        planned,
      };
    });

    const totalPending = Math.max(totalPlanned - totalCompleted, 0);
    const maxY = Math.max(...chartData.map(item => item.planned), 100);

    return (
      <div
        key={order}
        className="bg-white border rounded-lg shadow p-4 w-full"
      >
        <h3 className="text-md font-semibold text-indigo-700 mb-2">{order}</h3>
        <p className="text-sm text-gray-600 mb-1">
          📅 Month: {new Date().toLocaleString("default", { month: "long", year: "numeric" })}
        </p>

        <div className="text-sm text-gray-700 mb-3 flex flex-col gap-1">
          <span>📦 Total Order Quantity: <strong>{totalPlanned}</strong></span>
          <span>✅ Total Completed: <strong>{totalCompleted}</strong></span>
          <span>⏳ Total Pending: <strong>{totalPending}</strong></span>
        </div>

        <ResponsiveContainer width="100%" height={360}>
          <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="label" angle={-20} textAnchor="end" interval={0} height={40} />
            <YAxis domain={[0, maxY]} />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              layout="vertical"
              verticalAlign="bottom"
              align="left"
              iconType="circle"
              wrapperStyle={{
                paddingTop: 10,
                paddingLeft: 0,
                lineHeight: '22px',
              }}
            />
            <Bar dataKey="completed" stackId="a" fill={colorPalette[0]} name="✅ Completed" barSize={40}>
              <LabelList dataKey="planned" position="top" fontSize={12} formatter={val => (val > 0 ? val : "")} />
            </Bar>
            <Bar dataKey="pending" stackId="a" fill={colorPalette[1]} name="⏳ Pending" barSize={40} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  });

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center">
      <div className="bg-white w-full h-full p-6 overflow-y-auto relative rounded-none shadow-none">
        <button className="absolute top-4 right-6 text-2xl text-gray-500 hover:text-red-500 z-50" onClick={onClose}>
          ✖
        </button>
        <h2 className="text-2xl font-bold text-indigo-800 mb-6">📊 Monthly RTS Summary</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-6">
          {allCharts.length > 0 ? allCharts : (
            <p className="text-gray-500">No Ready to Sewing data available for this month.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default MonthlyReportGraphModal;
